/**
 * UI→worker playback tick cadence ({@link syncWebPlaybackTick} in VBuffer).
 *
 * Visible mid/high devices use rAF so frame dwell tracks paints. Low-capacity Android
 * (armeabi-v7a SlimBOX-class) cannot afford ~60 Comlink `tick()` RPCs/s on top of 1080p
 * decode — use a fixed interval aligned with the Android frame poll instead.
 *
 * Mid-tier with a video-heavy queue also uses the interval: dual-decoder preload + rAF
 * Comlink floods accelerate progressive jitter on ~2 GB / Mali-class sticks (PLAYER-1J).
 */

/** Hidden-tab fallback (browsers throttle rAF). */
export const WEB_PLAYBACK_TICK_HIDDEN_MS = 500

/**
 * Visible low-tier interval. Matches `ANDROID_FRAME_POLL_INTERVAL_MS` in PlaybackScheduler
 * so dwell accuracy stays comparable without flooding the main thread.
 */
export const WEB_PLAYBACK_TICK_LOW_CAPACITY_MS = 250

/** Mid-tier switches to the low-capacity interval when the queue has at least this many videos. */
export const WEB_PLAYBACK_TICK_MID_VIDEO_HEAVY_MIN = 3

export type WebPlaybackTickMode =
  | { kind: 'raf' }
  | { kind: 'interval', ms: number }

/**
 * Resolve how the UI thread should drive `playbackScheduler.tick`.
 * Unknown/`null` tier keeps rAF (same as mid/high) until capacity is classified.
 */
export function resolveWebPlaybackTickMode(input: {
  documentVisible: boolean
  capacityTier: 'low' | 'mid' | 'high' | null | undefined
  /** Count of video creatives in the materialized queue (optional; mid video-heavy throttle). */
  videoFrameCountInQueue?: number
}): WebPlaybackTickMode {
  if (!input.documentVisible) {
    return { kind: 'interval', ms: WEB_PLAYBACK_TICK_HIDDEN_MS }
  }
  if (input.capacityTier === 'low') {
    return { kind: 'interval', ms: WEB_PLAYBACK_TICK_LOW_CAPACITY_MS }
  }
  if (
    input.capacityTier === 'mid'
    && (input.videoFrameCountInQueue ?? 0) >= WEB_PLAYBACK_TICK_MID_VIDEO_HEAVY_MIN
  ) {
    return { kind: 'interval', ms: WEB_PLAYBACK_TICK_LOW_CAPACITY_MS }
  }
  return { kind: 'raf' }
}
